import React, { useState, useEffect } from 'react';
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import s from '../TourDetails/BookingDetailsPage.module.scss';
import { Button, Typography, Alert, CircularProgress, Box } from '@mui/material';

const API_BASE  = process.env.REACT_APP_API_LOCAL_URL;// ← your Laravel API URL

const PaymentForm = ({ email, name, onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();

    const [setupIntent, setSetupIntent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);

    // Create SetupIntent once on mount
    useEffect(() => {
        let ignore = false;

        const createSetup = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_BASE}stripe/create-setup`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, name }),
                });

                const data = await res.json();

                if (!ignore) {
                    if (data.success) {
                        setSetupIntent(data);
                    } else {
                        setError(data.error || 'Failed to initialize payment');
                    }
                }
            } catch (err) {
                if (!ignore) {
                    setError(err.message || 'Network error');
                }
            } finally {
                if (!ignore) {
                    setLoading(false);
                }
            }
        };

        createSetup();

        return () => {
            ignore = true;
        };
    }, [email, name]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements || !setupIntent) {
            setError('Payment system not ready');
            return;
        }

        setProcessing(true);
        setError(null); // Clear previous error

        const cardNumber = elements.getElement(CardNumberElement);

        try {
            const { error, setupIntent: confirmedSetup } = await stripe.confirmCardSetup(
                setupIntent.client_secret,
                {
                    payment_method: {
                        card: cardNumber,
                        billing_details: {
                            name,
                            email,
                        },
                    },
                }
            );

            if (error) {
                setError(error.message || 'Card validation failed');
                setProcessing(false);
                return;
            }

            // Success → pass payment_method ID to parent
            onSuccess(confirmedSetup.payment_method);
        } catch (err) {
            setError('An unexpected error occurred');
            console.error(err);
        } finally {
            setProcessing(false);
        }
    };

    // Show loading only while fetching SetupIntent
    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    安全な支払いフォームを準備しています…
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{  mx: 'auto', p: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 3 }}>
            <Typography variant="h5" gutterBottom align="center">
                安全なカード情報
            </Typography>

            <Typography variant="body2" color="text.secondary" align="center" paragraph>
                この予約のためにカード情報を安全に保存します。現在、料金は請求されません。
            </Typography>

            <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        カード番号 / Card Number
                    </Typography>
                    <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'white' }}>
                        <CardNumberElement
                            options={{
                                style: {
                                    base: { fontSize: '16px', color: '#32325d' },
                                    invalid: { color: '#ef4444' },
                                },
                            }}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            有効期限 / Expiry Date
                        </Typography>
                        <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'white' }}>
                            <CardExpiryElement
                                options={{
                                    style: {
                                        base: { fontSize: '16px', color: '#32325d' },
                                        invalid: { color: '#ef4444' },
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            セキュリティコード（CVC）
                        </Typography>
                        <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'white' }}>
                            <CardCvcElement
                                options={{
                                    style: {
                                        base: { fontSize: '16px', color: '#32325d' },
                                        invalid: { color: '#ef4444' },
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className={s.paymentButton}
                    disabled={processing || !stripe || !elements}
                    sx={{   fontWeight: 'bold' }}
                >
                    {processing ? '処理中…' : 'カードを保存して予約を確定する'}
                </Button>
            </form>

            <Typography variant="caption" display="block" align="center" sx={{ mt: 4, color: 'text.secondary' }}>
                Stripeによって保護されています • 現在は請求されません • 将来の利用のためにカードが保存されます
            </Typography>
        </Box>
    );
};

export default PaymentForm;